/*
  Cookie consent modal (D-54, Charles, 2026-11-03): sections/consent-modal.liquid.

  Replaces Shopify's native cookie banner, which keeps no consent log we can
  export, and talks to the same Shopify Customer Privacy API, so Shopify's own
  pixels and the apps read the visitor's choice exactly as before.

  <consent-modal>:
  - loads the API with Shopify.loadFeatures (consent-tracking-api 0.1) unless
    window.Shopify.customerPrivacy already exists;
  - opens the dialog when shouldShowBanner() is true and currentVisitorConsent()
    holds no answer yet; never by itself on the policy pages (data-policy-page);
  - shows screen 1's « Decline all » only when getRegion() is in the EEA, the UK
    or Switzerland (GDPR);
  - sends each choice with setTrackingConsent; sale_of_data follows marketing;
    the dialog closes only when Shopify's callback reports success, otherwise it
    stays open with a one-line message. Nothing reloads the page;
  - refuses Esc (cancel prevented) and has no close button; a backdrop click
    does nothing on a native dialog;
  - when data-log-url is set, sends one text/plain beacon per saved choice (no
    CORS preflight) with consent_id, analytics, marketing, preferences,
    sale_of_data, action, text_version, locale, region, path, client_ts. No IP,
    no email, nothing else.

  window.HeatloverConsent.open('preferences' | 'main') opens it on demand (the
  footer link); a `consent-modal:open` event with detail.screen does the same.
  window.HeatloverConsent.pending() is true while the visitor still owes an
  answer, so assets/signup-popup.js never opens before or over this modal.

  `?consent_preview=1` opens screen 1 on load even when a choice is stored,
  `?consent_preview=prefs` opens screen 2, so the modal can be reviewed.

  The decisions themselves sit in HeatloverConsentLogic, pure functions with no
  DOM, pinned by tests/browser/consent_modal_logic.test.mjs.
*/
(function () {
  // EU 27, the rest of the EEA (Iceland, Liechtenstein, Norway), the UK and Switzerland.
  const GDPR_COUNTRIES = [
    'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE',
    'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
    'IS', 'LI', 'NO',
    'GB', 'CH',
  ];
  const CATEGORIES = ['analytics', 'marketing', 'preferences'];

  const logic = {
    GDPR_COUNTRIES,

    // getRegion() gives the country code, then the region code when there is one: CAQC, FRIDF, GB.
    isGdprRegion(region) {
      if (typeof region !== 'string' || region.length < 2) return false;
      return GDPR_COUNTRIES.includes(region.slice(0, 2).toUpperCase());
    },

    // currentVisitorConsent() returns 'yes', 'no' or '' per category; '' everywhere means no answer.
    // A sale-of-data opt-out alone (a US privacy link) is not an answer to this modal.
    hasAnswer(consent) {
      if (!consent) return false;
      return CATEGORIES.some((name) => consent[name] === 'yes' || consent[name] === 'no');
    },

    shouldShow({ bannerNeeded, consent, preview }) {
      if (preview) return true;
      return Boolean(bannerNeeded) && !logic.hasAnswer(consent);
    },

    unanswered(api) {
      if (!api) return false;
      return Boolean(api.shouldShowBanner()) && !logic.hasAnswer(api.currentVisitorConsent());
    },

    // Off unless the visitor said yes (Law 25: privacy by default).
    togglesFrom(consent) {
      const out = {};
      CATEGORIES.forEach((name) => {
        out[name] = Boolean(consent) && consent[name] === 'yes';
      });
      return out;
    },

    choiceFor(action, toggles) {
      if (action === 'accept') return { analytics: true, marketing: true, preferences: true, sale_of_data: true };
      if (action === 'decline_all') {
        return { analytics: false, marketing: false, preferences: false, sale_of_data: false };
      }
      if (action === 'save') {
        const marketing = Boolean(toggles.marketing);
        return {
          analytics: Boolean(toggles.analytics),
          marketing,
          preferences: Boolean(toggles.preferences),
          sale_of_data: marketing,
        };
      }
      throw new Error(`Unknown consent action: ${action}`);
    },

    // Read off the query string by hand: the logic runs where URLSearchParams may not exist.
    previewScreen(search) {
      const match = /[?&]consent_preview=([^&#]*)/.exec(search || '');
      const value = match ? match[1] : null;
      if (value === '1') return 'main';
      if (value === 'prefs') return 'preferences';
      return null;
    },

    buildPayload({ choice, action, consentId, textVersion, locale, region, path, now }) {
      return {
        consent_id: consentId || null,
        analytics: choice.analytics,
        marketing: choice.marketing,
        preferences: choice.preferences,
        sale_of_data: choice.sale_of_data,
        action,
        text_version: textVersion,
        locale,
        region: region || null,
        path,
        client_ts: now.toISOString(),
      };
    },

    // setTrackingConsent's callback receives nothing, or an object, on success; an error otherwise.
    // The toString test recognises an Error from any realm (a frame, the Node test's context).
    isFailure(result) {
      if (!result) return false;
      return Object.prototype.toString.call(result) === '[object Error]' || Boolean(result.error);
    },
  };

  globalThis.HeatloverConsentLogic = logic;

  // Below this line is the browser; a bare Node context (the logic test) stops here.
  if (typeof customElements === 'undefined' || customElements.get('consent-modal')) return;

  function loadPrivacyApi() {
    return new Promise((resolve, reject) => {
      const shopify = window.Shopify;
      if (shopify && shopify.customerPrivacy) {
        resolve(shopify.customerPrivacy);
        return;
      }
      if (!shopify || typeof shopify.loadFeatures !== 'function') {
        reject(new Error('Shopify.loadFeatures is unavailable'));
        return;
      }
      shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        if (error || !window.Shopify.customerPrivacy) {
          reject(error || new Error('customerPrivacy did not load'));
          return;
        }
        resolve(window.Shopify.customerPrivacy);
      });
    });
  }

  customElements.define(
    'consent-modal',
    class ConsentModal extends HTMLElement {
      connectedCallback() {
        if (this.dialog) return;
        this.dialog = this.querySelector('dialog');
        this.status = this.querySelector('[data-consent-status]');
        this.screens = [...this.querySelectorAll('[data-consent-screen]')];
        this.toggles = [...this.querySelectorAll('input[role="switch"]')];
        this.api = null;
        this.apiState = 'loading';
        this.allowClose = false;

        // Esc: the gate stays up until the visitor chooses.
        this.dialog.addEventListener('cancel', (event) => event.preventDefault());
        // Some browsers let a repeated Esc through anyway; anything but a saved choice reopens it.
        // The close event fires asynchronously, after close() has returned, so the flag is
        // consumed here rather than reset in close() (found in Chrome on the preview, 2026-11-03).
        this.dialog.addEventListener('close', () => {
          if (this.allowClose) {
            this.allowClose = false;
            return;
          }
          this.dialog.showModal();
        });
        this.dialog.addEventListener('keydown', (event) => this.trapFocus(event));

        this.querySelectorAll('[data-consent-action]').forEach((button) =>
          button.addEventListener('click', () => this.choose(button.dataset.consentAction))
        );
        this.querySelectorAll('[data-consent-show]').forEach((button) =>
          button.addEventListener('click', () => this.showScreen(button.dataset.consentShow, true))
        );

        window.HeatloverConsent = {
          open: (screen) => this.open(screen),
          pending: () => this.pending(),
        };
        document.addEventListener('consent-modal:open', (event) =>
          this.open(event.detail && event.detail.screen)
        );

        const preview = logic.previewScreen(window.location.search);
        this.ready = loadPrivacyApi().then(
          (api) => {
            this.api = api;
            this.apiState = 'ready';
            this.applyRegion();
            return api;
          },
          () => {
            // No API (blocked script, old browser): nothing to gate, and nothing would save.
            this.apiState = 'failed';
            return null;
          }
        );

        if (window.Shopify && window.Shopify.designMode) {
          document.addEventListener('shopify:section:select', (event) => {
            if (this.closest(`#shopify-section-${event.detail.sectionId}`)) this.open('main');
          });
          document.addEventListener('shopify:section:deselect', () => this.close());
          return;
        }

        this.ready.then((api) => {
          if (preview) {
            this.open(preview);
            return;
          }
          if (!api || this.hasAttribute('data-policy-page')) return;
          const show = logic.shouldShow({
            bannerNeeded: api.shouldShowBanner(),
            consent: api.currentVisitorConsent(),
            preview,
          });
          if (show) this.open('main');
        });
      }

      // True while the visitor still owes an answer, and while the API is still loading.
      pending() {
        if (this.apiState === 'loading') return true;
        if (this.apiState === 'failed') return false;
        try {
          return logic.unanswered(this.api);
        } catch (error) {
          return false;
        }
      }

      region() {
        try {
          return this.api && typeof this.api.getRegion === 'function' ? this.api.getRegion() : null;
        } catch (error) {
          return null;
        }
      }

      applyRegion() {
        const gdpr = logic.isGdprRegion(this.region());
        this.querySelectorAll('[data-consent-gdpr]').forEach((button) => {
          button.hidden = !gdpr;
        });
      }

      // The toggles show what is stored, or off on a first visit.
      syncToggles() {
        let consent = null;
        try {
          consent = this.api ? this.api.currentVisitorConsent() : null;
        } catch (error) {
          consent = null;
        }
        const state = logic.togglesFrom(consent);
        this.toggles.forEach((toggle) => {
          toggle.checked = state[toggle.name];
        });
      }

      readToggles() {
        const out = {};
        this.toggles.forEach((toggle) => {
          out[toggle.name] = toggle.checked;
        });
        return out;
      }

      async open(screen) {
        await this.ready;
        this.syncToggles();
        this.clearStatus();
        if (!this.dialog.open) this.dialog.showModal();
        this.showScreen(screen === 'preferences' ? 'preferences' : 'main', true);
      }

      close() {
        if (!this.dialog.open) return;
        this.allowClose = true;
        this.dialog.close();
      }

      showScreen(name, moveFocus) {
        this.screens.forEach((screen) => {
          screen.hidden = screen.dataset.consentScreen !== name;
        });
        this.dialog.setAttribute(
          'aria-labelledby',
          name === 'preferences' ? 'ConsentModal-prefs-title' : 'ConsentModal-title'
        );
        this.clearStatus();
        if (!moveFocus) return;
        const current = this.screens.find((screen) => !screen.hidden);
        const first = current && current.querySelector('button:not([hidden]), input');
        if (first) first.focus();
      }

      setBusy(busy) {
        this.querySelectorAll('[data-consent-action]').forEach((button) => {
          button.disabled = busy;
        });
      }

      showError() {
        this.status.textContent = this.status.dataset.error;
      }

      clearStatus() {
        this.status.textContent = '';
      }

      choose(action) {
        const choice = logic.choiceFor(action, this.readToggles());
        this.clearStatus();
        if (!this.api) {
          this.showError();
          return;
        }
        this.setBusy(true);
        const done = (result) => {
          this.setBusy(false);
          if (logic.isFailure(result)) {
            this.showError();
            return;
          }
          this.sendLog(action, choice);
          this.close();
        };
        try {
          this.api.setTrackingConsent(choice, done);
        } catch (error) {
          done(error);
        }
      }

      sendLog(action, choice) {
        const url = this.dataset.logUrl;
        if (!url || !navigator.sendBeacon) return;
        let consentId = null;
        try {
          consentId = typeof this.api.consentId === 'function' ? this.api.consentId() : null;
        } catch (error) {
          consentId = null;
        }
        const payload = logic.buildPayload({
          choice,
          action,
          consentId,
          textVersion: this.dataset.textVersion,
          locale: this.dataset.locale,
          region: this.region(),
          path: window.location.pathname,
          now: new Date(),
        });
        try {
          navigator.sendBeacon(url, new Blob([JSON.stringify(payload)], { type: 'text/plain' }));
        } catch (error) {
          // The log is best effort; the choice itself is already saved with Shopify.
        }
      }

      // showModal already makes the page behind inert; this keeps Tab cycling inside the dialog
      // instead of escaping to the browser's own controls (the sign-up pop-up's trap).
      trapFocus(event) {
        if (event.key !== 'Tab') return;
        const focusable = [
          ...this.dialog.querySelectorAll('button, [href], input:not([type="hidden"]), [tabindex]:not([tabindex="-1"])'),
        ].filter((el) => !el.disabled && el.tabIndex !== -1 && el.getClientRects().length);
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && (document.activeElement === first || document.activeElement === this.dialog)) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }
  );
})();
